<template>
  <Transition name="drawer" @after-leave="drawerClosed">
    <div
      v-show="filterStore.openFilterSideDrawer"
      class="fixed top-0 right-0 z-50 h-full w-full max-w-[300px] transform overflow-y-auto bg-white shadow-lg transition duration-300 ease-out laptop:hidden"
    >
      <Transition name="slide" mode="out-in">
        <div v-if="!filterStore.activeFilterSideDrawerTab" class="flex h-full flex-col">
          <div class="flex min-h-[60px] items-center justify-between p-4">
            <h3 class="font-semibold uppercase">{{ $t("filter") }}</h3>
            <button
              v-if="filterStore.getNumberOfActiveCategories || router.currentRoute.value.query.brand"
              class="flex items-center border border-ng70 px-1.5 py-1 uppercase text-ng400"
              @click="clearAllFilters()"
            >
              <Icon name="bi:x" size="14" />

              <span class="ml-1.5 text-xs desktop:text-sm">
                {{ $t("clear-all") }}
              </span>
            </button>
          </div>
          <div class="h-full bg-ng30 py-2">
            <ul class="bg-white">
              <li
                v-for="filterParent in props.filters"
                :key="filterParent.id"
                :class="{
                  'disabled pointer-events-none opacity-50': filterParent.filters.length === 0,
                }"
                class="flex flex-col border-b border-ng30 transition duration-100 ease-out hover:bg-[#f8f8f8]"
              >
                <button class="py-2 px-4 text-left" @click="showFilter(filterParent)">
                  <span class="text-base capitalize text-n700">{{ filterParent.name }}</span>

                  <div class="mb-1">
                    <ul class="flex flex-wrap items-start justify-start" v-if="filterParent.slug !== 'brands'">
                      <li
                        v-for="(filter, index) in filterStore.getActiveCategoryFilters(filterParent)"
                        :key="filter.slug"
                        class="flex items-center justify-center text-sm text-[#acacac]"
                      >
                        <button>
                          {{ filter.name }}
                          <span v-if="index !== filterStore?.getActiveCategoryFilters(filterParent)?.length - 1"
                            >,&nbsp;</span
                          >
                        </button>
                      </li>
                    </ul>
                    <div v-else>
                      <span class="text-sm text-[#acacac]">
                        {{ selectedBrand?.attributes?.name }}
                      </span>
                    </div>
                  </div>
                </button>
              </li>
            </ul>
          </div>
        </div>
      </Transition>

      <Transition name="slide-left" mode="out-in">
        <div
          class="absolute top-0 left-0 flex h-full w-full flex-col"
          v-if="filterStore.activeFilterSideDrawerTab !== null"
        >
          <div class="flex min-h-[60px] items-center justify-between px-2 py-4">
            <button class="flex items-center text-ng700 focus:outline-none" @click="filterStore.clearActiveFilterTab">
              <Icon name="ep:back" size="22px" />
              <h3 class="ml-2">{{ filterStore.activeFilterSideDrawerTab.name }}</h3>
            </button>
            <Transition name="fade">
              <button
                v-if="filterStore.activeFilterSideDrawerTab.slug !== 'brands'"
                class="flex items-center border border-ng70 px-1.5 py-1 uppercase text-ng400"
                @click="checkAllNoneFilters(filterStore.activeFilterSideDrawerTab)"
              >
                <template
                  v-if="filterStore?.getActiveCategoryFilters(filterStore.activeFilterSideDrawerTab)?.length === 0"
                >
                  <Icon name="bi:check" size="14" />
                </template>
                <template v-else>
                  <Icon name="bi:x" size="14" />
                </template>
                <span class="ml-1.5 text-xs desktop:text-sm">
                  {{
                    $t(
                      "all-clear",
                      filterStore?.getActiveCategoryFilters(filterStore.activeFilterSideDrawerTab)?.length || 0
                    )
                  }}
                </span>
              </button>
            </Transition>
          </div>
          <div class="h-full bg-ng30 py-2">
            <ul class="bg-white">
              <li
                v-for="filter in filterStore.activeFilterSideDrawerTab.filters"
                :key="filter.slug"
                class="flex flex-col border-b border-ng30 transition duration-100 ease-out"
              >
                <button
                  v-if="filterStore.activeFilterSideDrawerTab.slug !== 'brands'"
                  class="py-2 px-4 text-left hover:bg-ng100"
                  @click="updateFilters(filterStore.activeFilterSideDrawerTab, filter)"
                  :class="{
                    'bg-ng40 ': filterStore.isFilterActive(filterStore.activeFilterSideDrawerTab, filter),
                    '': !filterStore.isFilterActive(filterStore.activeFilterSideDrawerTab, filter),
                  }"
                >
                  {{ filter.name }}
                </button>
                <button
                  v-else-if="filterStore.activeFilterSideDrawerTab.slug === 'brands'"
                  class="py-2 px-4 text-left hover:bg-ng100"
                  @click="updateFilters(filterStore.activeFilterSideDrawerTab, filter)"
                  :class="{
                    'bg-ng40 ': selectedBrand?.attributes?.slug === filter.slug,
                    '': selectedBrand?.attributes?.slug !== filter.slug,
                  }"
                >
                  {{ filter.name }}
                </button>
              </li>
            </ul>
          </div>
        </div>
      </Transition>
    </div>
  </Transition>
  <Teleport to="body">
    <div
      class="fixed inset-0 z-10 bg-[#000]/25 bg-opacity-50 transition-opacity duration-300 laptop:hidden"
      :class="{
        'pointer-events-none opacity-0': !filterStore.openFilterSideDrawer,
        'pointer-events-auto opacity-100': filterStore.openFilterSideDrawer,
      }"
      @click="filterStore.toggleFiltersSideDrawer(false)"
    ></div>
  </Teleport>
</template>
<script setup lang="ts">
import { useFilters } from "~~/stores/filters";
import { SelectedBrandKey } from "~/symbols/symbols";

const filterStore = useFilters();
const { t: $t } = useI18n();

function showFilter(filterParent: FilterCategory | null) {
  if (!filterParent) return;
  filterStore.setActiveFilterTab(filterParent);
}
const selectedBrand = inject(SelectedBrandKey);

function checkAllNoneFilters(filterParent: FilterCategory | null) {
  if (!filterParent) return;
  if (filterStore.getActiveCategoryFilters(filterParent).length) {
    filterStore.clearAllCategoryFilters(filterParent.id);
  } else {
    filterStore.addAllFilters(filterParent.id, filterParent.slug, filterParent.filters as Filter[]);
  }
}
const router = useRouter();
function updateFilters(filterCategory: FilterCategory | null, filter: Filter) {
  if (filterCategory?.slug === "brands") {
    if (selectedBrand?.value?.attributes?.slug === filter.slug) {
      router.replace({ query: undefined });

      return;
    }
    router.push({ query: { brand: filter.slug } });
  } else {
    filterStore.activateFilter(filterCategory, filter);
  }
}

function clearAllFilters() {
  router.replace({ query: undefined });

  // filterStore.clearAllFilters();
}

function drawerClosed() {
  filterStore.clearActiveFilterTab();
}

const props = defineProps<{
  filters: FilterCategory[];
}>();
</script>
<style lang="scss" scoped>
:global(.slide-enter-active),
:global(.slide-leave-active) {
  transition: transform 215ms cubic-bezier(0, 0, 0.2, 1) 0ms;
}

:global(.slide-enter-from),
:global(.slide-leave-to) {
  transform: translateX(-100%);
}

:global(.slide-left-enter-active),
:global(.slide-left-leave-active) {
  transition: transform 215ms cubic-bezier(0, 0, 0.2, 1) 0ms, opacity 215ms cubic-bezier(0, 0, 0.2, 1) 0ms;
}
:global(.slide-left-enter-from) {
  transform: translateX(-100%);
  opacity: 0;
}

:global(.slide-left-enter-from),
:global(.slide-left-leave-to) {
  transform: translateX(100%);
}

:global(.drawer-enter-active),
:global(.drawer-leave-active) {
  transition: transform 215ms cubic-bezier(0, 0, 0.2, 1) 0ms;
}

:global(.drawer-enter-from),
:global(.drawer-leave-to) {
  transform: translateX(100%);
}
</style>
